/** Persist last ledger entity + tax year so Sidebar / bare routes keep scope. */

const YEAR_KEY = 'cwy_ledger_year';
const ENTITY_KEY = 'cwy_ledger_entityId';
const LEGACY_ACCOUNT_KEY = 'cwy_ledger_accountId';
const LEGACY_COMPANY_KEY = 'cwy_ledger_companyId';

export type LedgerBookSession = {
  year: number | null;
  entityId: string | null;
};

export function readLedgerBookSession(): LedgerBookSession {
  try {
    const yearRaw = sessionStorage.getItem(YEAR_KEY);
    const year = yearRaw && !Number.isNaN(parseInt(yearRaw, 10)) ? parseInt(yearRaw, 10) : null;
    const entityId =
      sessionStorage.getItem(ENTITY_KEY) ||
      sessionStorage.getItem(LEGACY_COMPANY_KEY);
    return {
      year,
      entityId,
    };
  } catch {
    return { year: null, entityId: null };
  }
}

export function persistLedgerBookSession(next: {
  year?: number | null;
  entityId?: string | null;
}): void {
  try {
    if (next.year != null && !Number.isNaN(next.year) && next.year > 0) {
      sessionStorage.setItem(YEAR_KEY, String(next.year));
    }
    if (next.entityId) {
      sessionStorage.setItem(ENTITY_KEY, next.entityId);
    } else if (next.entityId === null) {
      sessionStorage.removeItem(ENTITY_KEY);
    }
    sessionStorage.removeItem(LEGACY_ACCOUNT_KEY);
    sessionStorage.removeItem(LEGACY_COMPANY_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Paths that should carry ledger book query params from session. */
export function isLedgerBookScopedPath(path: string): boolean {
  const base = path.split('?')[0];
  return (
    base === '/app/ledger' ||
    base.startsWith('/app/ledger/') ||
    base === '/app/payments' ||
    base === '/app/payouts' ||
    base === '/app/expenses' ||
    base === '/app/outflow' ||
    base === '/app/collections'
  );
}

/** Append session entityId/year if path has no query yet. */
export function withLedgerBookQuery(path: string): string {
  if (!isLedgerBookScopedPath(path)) return path;
  if (path.includes('?')) return path;

  const { year, entityId } = readLedgerBookSession();
  const params = new URLSearchParams();
  if (year != null) params.set('year', String(year));
  if (entityId) params.set('entityId', entityId);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
